import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiShield, FiDollarSign, FiHeart, FiCheckCircle, FiArrowRight } = FiIcons;

const ProductGrid = () => {
  const products = [
    {
      icon: FiClock,
      title: 'Term Life Insurance',
      description: 'Affordable coverage for 10, 20, or 30 years. Perfect for growing families and mortgage protection.',
      features: ['Coverage up to $3 million', 'Fixed premiums', 'No medical exam for most'],
      price: 'Starting at $15/month',
      link: '/term-life-insurance',
      color: 'text-blue-600'
    },
    {
      icon: FiShield,
      title: 'Whole Life Insurance',
      description: 'Lifetime protection with cash value growth. Guaranteed approval for ages 66-85.',
      features: ['Lifetime coverage', 'Cash value accumulation', 'Guaranteed approval'],
      price: 'Starting at $50/month',
      link: '/whole-life-insurance',
      color: 'text-green-600'
    },
    {
      icon: FiDollarSign,
      title: 'Million Dollar Coverage',
      description: 'High-value protection for executives and high earners. Estate planning and wealth transfer.',
      features: ['Up to $3 million coverage', 'Competitive rates', 'Fast approval'],
      price: 'Starting at $65/month',
      link: '/million-dollar-life-insurance',
      color: 'text-purple-600'
    },
    {
      icon: FiHeart,
      title: 'Final Expense Insurance',
      description: 'Cover funeral costs and final expenses. No medical exam, guaranteed acceptance.',
      features: ['$1,000 to $30,000 coverage', 'Immediate coverage', 'Easy application'],
      price: 'Starting at $25/month',
      link: '/final-expense-insurance',
      color: 'text-red-600'
    },
    {
      icon: FiCheckCircle,
      title: 'No Medical Exam Insurance',
      description: 'Get covered instantly without medical exams or blood tests. Apply online in minutes.',
      features: ['No medical exam', 'Instant approval', 'Coverage up to $1 million'],
      price: 'Starting at $20/month',
      link: '/no-medical-exam-insurance',
      color: 'text-orange-600'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Life Insurance Products for Every California Family
          </h2>
          <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
            From young professionals to retirees, we have the right coverage for your life stage and budget.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border border-secondary-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center mb-4">
                <div className={`p-3 rounded-lg bg-opacity-10 ${product.color.replace('text-', 'bg-')}`}>
                  <SafeIcon icon={product.icon} className={`h-6 w-6 ${product.color}`} />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 ml-3">{product.title}</h3>
              </div>
              <p className="text-secondary-600 mb-4">{product.description}</p>
              <ul className="space-y-2 mb-6">
                {product.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-secondary-700">
                    <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-green-500 mr-2" />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="border-t border-secondary-200 pt-4">
                <p className="text-lg font-semibold text-primary-600 mb-3">{product.price}</p>
                <Link
                  to={product.link}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                >
                  Learn More
                  <SafeIcon icon={FiArrowRight} className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a 
            href="https://quote.burbankinsuranceservices.com/Dt2tCn" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors shadow-lg"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductGrid;